export async function DOMReady(): Promise<void> {
    return new Promise(resolve => {
        if (document.readyState !== "loading") {
            resolve()
        } else {
            window.addEventListener("DOMContentLoaded", () => {
                resolve()
            })
        }
    })
}

export type DOMTarget = string | Element

export function findAll<T extends Element>(selector: DOMTarget, filterType?: { new (): T }): T[] {
    const elements = typeof selector === "string" ? Array.from(document.querySelectorAll(selector)) : [selector]
    return elements.filter((v): v is T => (filterType ? v instanceof filterType : true))
}

export function parents(target: DOMTarget, selector?: string): Element[] {
    let parentList: Element[] = []
    findAll(target).forEach(element => {
        const parent = element.parentNode
        if (parent !== document && parent instanceof Element) {
            parentList.push(parent)
            parentList = parentList.concat(parents(parent))
        }
    })
    return parentList.filter(element => selector === undefined || matches(element, selector))
}

export function matches(target: DOMTarget, selector: string): boolean {
    const matchesFunc =
        Element.prototype.matches ||
        // @ts-expect-error proprietary field
        Element.prototype.msMatchesSelector ||
        Element.prototype.webkitMatchesSelector
    return findAll(target).some(element => matchesFunc.call(element, selector))
}

export function bindClickOutside([element, input]: HTMLElement[], callback: () => void) {
    const onClick = (event: MouseEvent) => {
        const target = event.target

        if (target instanceof HTMLElement && element) {
            if (target !== element && target !== input && !parents(target).includes(element)) {
                callback()
            }
        }
    }

    document.addEventListener("click", onClick)

    return {
        destroy: () => {
            document.removeEventListener("click", onClick)
        }
    }
}

export function bindBlur(element: HTMLElement, callback: () => void): void {
    element.tabIndex = 0
    element.addEventListener("blur", callback)
}

export function unhideNativeContent(cssSelector: string | undefined) {
    if (!cssSelector) {
        return
    }
    const target = document.querySelector(cssSelector)
    if (!target) {
        return
    }
    target.classList.remove("ns-content-hidden")
}

export function cloneNode<T extends Node>(node: T, deep: boolean): T {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return node.cloneNode(deep) as T
}

const scrollPosStorageKey = "nosto:search-templates:scrollPos"

type ScrollSaveOptions = {
    position: number
    href: string
}

export function savePageScroll() {
    window.sessionStorage.setItem(
        scrollPosStorageKey,
        JSON.stringify({
            position: window.scrollY,
            href: window.location.href
        })
    )
}

export function isBackOrForwardNavigation() {
    const [navEntry] = performance.getEntriesByType("navigation")

    if (navEntry && "type" in navEntry) {
        return navEntry.type === "back_forward"
    }

    return false
}

function retrieveSavedScroll() {
    const savedScroll = window.sessionStorage.getItem(scrollPosStorageKey)
    if (!savedScroll) {
        return undefined
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return JSON.parse(savedScroll) as ScrollSaveOptions
}

export function restoreSavedScroll() {
    try {
        const scrollPos = retrieveSavedScroll()
        if (
            !scrollPos ||
            !scrollPos.position ||
            scrollPos.href !== window.location.href ||
            !isBackOrForwardNavigation()
        ) {
            return
        }

        const interval = window.setInterval(() => {
            const maxScrollable = document.documentElement.scrollHeight - window.innerHeight
            if (maxScrollable >= scrollPos.position) {
                window.scrollTo(0, scrollPos.position)
                window.sessionStorage.removeItem(scrollPosStorageKey)
                window.clearInterval(interval)
            }
        }, 10)

        /**
         * Defensive block in case scroll position couldn't be restored shortly after page load.
         */
        window.setTimeout(() => {
            window.clearInterval(interval)
            console.warn("Scroll position couldn't be restored in 5 seconds, something may be wrong.")
        }, 5000)
    } catch (error) {
        console.warn("Failed to restore scroll position:", error)
    }
}
